import React, { useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { CheckCircle, Download, ArrowRight, Calendar, FileText } from "lucide-react";
import { API, getContentSource } from "@/lib/constants";
import PageMeta from "@/components/PageMeta";

const PDF_FALLBACK = "https://customer-assets.emergentagent.com/job_c98471e5-8255-466a-ba2a-37b6ef749830/artifacts/24xns8v4_100-Hooks-That-Stop-The-Scroll.pdf";const LEAD_COPY = {
  contact: { title: "Message Received!", desc: "Thanks for reaching out. A member of our team will get back to you within 24 hours." },
  audit: { title: "Your Free AI Visibility Audit Is On Its Way!", desc: "Our team will analyze your site's SEO, AEO, and GEO readiness and email your personalized audit within 48-72 hours." },
  quote: { title: "Quote Request Received!", desc: "We'll review your project details and send a custom quote within 24 hours." },
  mockup: { title: "Mockup Request Received!", desc: "Our team will analyze your website and send a customized AEO/GEO readiness mockup within 48-72 hours." },
  service: { title: "Project Questionnaire Received!", desc: "We'll review your answers and follow up with tailored recommendations within 24 hours." },
  guide: { title: "Your Guide Is On Its Way!", desc: "Check your inbox — your download link is headed to your email right now." },
  default: { title: "Thank You!", desc: "Your submission has been received. We'll be in touch shortly." },
};

const NEXT_STEPS = [
  { num: "1", text: "Our team reviews your submission" },
  { num: "2", text: "We reach out within 24 hours (48-72 for audits & mockups)" },
  { num: "3", text: "You get a clear, no-obligation plan for your next move" },
];

export default function ConversionThankYouPage({ variant = "lead" }) {
  const [searchParams] = useSearchParams();
  const from = searchParams.get("from") || "default";
  const copy = LEAD_COPY[from] || LEAD_COPY.default;
  const isSwipeFile = variant === "swipe-file";
  const downloadUrl = isSwipeFile ? (localStorage.getItem("dmn_swipefile_100hooks") || PDF_FALLBACK) : null;
  const fired = useRef(false);

  // Fire GA4 + Meta Pixel lead conversion events, segmented by lead type
  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    const leadType = isSwipeFile ? "swipe-file" : from;
    if (window.gtag) {
      window.gtag("event", "generate_lead", {
        lead_type: leadType,
        page_path: window.location.pathname + window.location.search,
      });
    }
    if (window.fbq) {
      window.fbq("track", "Lead", {
        content_name: leadType,
        content_category: isSwipeFile ? "lead_magnet" : "lead_form",
      });
    }
    axios.post(`${API}/conversions/track`, {
      lead_type: leadType,
      page_path: window.location.pathname + window.location.search,
      content_source: getContentSource(),
    }).catch(() => {});
  }, [isSwipeFile, from]);

  return (
    <div className="min-h-[70vh] pt-28 pb-16 flex items-center justify-center px-4" data-testid={isSwipeFile ? "thank-you-swipefile-page" : "thank-you-lead-page"}>
      <PageMeta
        title={isSwipeFile ? "Your Swipe File Is Ready! | Digital Media Ninja" : "Thank You! | Digital Media Ninja"}
        robots="noindex, nofollow"
      />

      <div className="max-w-xl w-full text-center">
        <div className="w-20 h-20 bg-[#00E676]/15 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-[#00E676]" />
        </div>

        {isSwipeFile ? (
          <>
            <h1 className="text-3xl sm:text-4xl font-bold mb-4">Your 100-Hook Swipe File Is Ready!</h1>
            <p className="text-gray-400 mb-8 leading-relaxed">
              Ten goals, 100 fill-in-the-blank templates, and the psychology behind each one — so you never stare at a blank caption again.
            </p>
            <a href={downloadUrl} target="_blank" rel="noopener noreferrer" data-testid="swipefile-thankyou-download-btn">
              <Button className="btn-ninja px-8 py-6 text-base mb-6">
                <Download className="w-5 h-5 mr-2" /> Download the Swipe File (PDF)
              </Button>
            </a>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm">
              <Link to="/blog/social-media-hooks-that-stop-the-scroll" className="text-gray-400 hover:text-[#00E676] transition-colors inline-flex items-center gap-1" data-testid="swipefile-back-to-article-link">
                <FileText className="w-4 h-4" /> Back to the full hooks guide
              </Link>
              <span className="hidden sm:block text-gray-700">|</span>
              <Link to="/contact" className="text-gray-400 hover:text-[#00E676] transition-colors inline-flex items-center gap-1" data-testid="swipefile-work-with-us-link">
                Want us to write them for you? <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </>
        ) : (
          <>
            <h1 className="text-3xl sm:text-4xl font-bold mb-4" data-testid="thank-you-lead-title">{copy.title}</h1>
            <p className="text-gray-400 mb-8 leading-relaxed">{copy.desc}</p>

            <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6 mb-8 text-left">
              <p className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-4">What happens next</p>
              <ul className="space-y-3">
                {NEXT_STEPS.map((s) => (
                  <li key={s.num} className="flex items-start gap-3 text-sm text-gray-300">
                    <span className="w-6 h-6 rounded-full bg-[#00E676]/15 text-[#00E676] font-bold text-xs flex items-center justify-center shrink-0 mt-0.5">{s.num}</span>
                    {s.text}
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contact" data-testid="thank-you-book-call-btn">
                <Button className="btn-ninja px-6">
                  <Calendar className="w-4 h-4 mr-2" /> Book a Strategy Call
                </Button>
              </Link>
              <Link to="/blog" className="text-gray-400 hover:text-[#00E676] transition-colors text-sm inline-flex items-center gap-1" data-testid="thank-you-browse-blog-link">
                Browse our latest guides <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
